/**
 * Painel de Análise de Padrões de Fala
 * Identifica hesitações, interrupções, repetições e outros padrões
 */

import React, { useState, useEffect, memo } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import VirtualizedAnalysisList from './VirtualizedAnalysisList'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import BlockIcon from '@mui/icons-material/Block'
import ReplayIcon from '@mui/icons-material/Replay'
import CancelIcon from '@mui/icons-material/Cancel'
import CampaignIcon from '@mui/icons-material/Campaign'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import RefreshIcon from '@mui/icons-material/Refresh'
import PersonIcon from '@mui/icons-material/Person'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { SpeechPatternAnalysis, SpeechPattern, SpeechPatternType } from '../../../types/transcription'
import { getSpeechPatternAnalysis, runOnDemandAnalysis } from '../../../services/api'
import AnalysisOutdatedBanner from './AnalysisOutdatedBanner'
import { contextualErrors, FriendlyError } from '../../../services/errorHandler'
import AnalysisNotAvailableCard from './AnalysisNotAvailableCard'
import AnalysisSkippedCard from './AnalysisSkippedCard'
import AnalysisProcessingCard from './AnalysisProcessingCard'
import { SpeakerMapping } from '../../../services/exportService'

type Props = {
  fileId: string
  onNavigateToSegment?: (segmentIndex: number) => void
  speakerMapping?: SpeakerMapping
  /** Status atual desta análise (do polling) */
  analysisTypeStatus?: string
  /** Callback quando análise on-demand é iniciada */
  onAnalysisStarted?: () => void
}

const PATTERN_CONFIG: Record<SpeechPatternType, {
  label: string
  icon: React.ElementType
  color: string
  bgColor: string
  description: string
}> = {
  hesitation: {
    label: 'Hesitação',
    icon: PauseCircleOutlineIcon,
    color: '#9c27b0',
    bgColor: '#f3e5f5',
    description: 'Pausas e marcadores de incerteza (éé, hmm, bem...)'
  },
  interruption: {
    label: 'Interrupção',
    icon: BlockIcon,
    color: '#f44336',
    bgColor: '#ffebee',
    description: 'Falante cortado ou sobreposto por outro'
  },
  repetition: {
    label: 'Repetição',
    icon: ReplayIcon,
    color: '#ff9800',
    bgColor: '#fff3e0',
    description: 'Palavras ou frases repetidas'
  },
  negation: {
    label: 'Negação',
    icon: CancelIcon,
    color: '#795548',
    bgColor: '#efebe9',
    description: 'Negativas enfáticas ou persistentes'
  },
  emphasis: {
    label: 'Ênfase',
    icon: CampaignIcon,
    color: '#2196f3',
    bgColor: '#e3f2fd',
    description: 'Tom elevado ou palavras enfatizadas'
  },
  uncertainty: {
    label: 'Incerteza',
    icon: HelpOutlineIcon,
    color: '#607d8b',
    bgColor: '#eceff1',
    description: 'Expressões de dúvida (acho que, talvez, não sei...)'
  },
  aggression: {
    label: 'Agressividade',
    icon: WarningAmberIcon,
    color: '#d32f2f',
    bgColor: '#ffcdd2',
    description: 'Tom agressivo ou linguagem hostil'
  }
}

const SIGNIFICANCE_CONFIG: Record<string, { label: string; color: string; weight: number }> = {
  high: { label: 'Alta', color: '#d32f2f', weight: 3 },
  medium: { label: 'Média', color: '#ff9800', weight: 2 },
  low: { label: 'Baixa', color: '#4caf50', weight: 1 }
}

/**
 * Formata o tempo para exibição.
 * Aceita tanto segundos (número ou string numérica) quanto formato HH:MM:SS.
 */
function formatTime(time: string): string {
  if (!time) return '0:00'
  
  // Se já está no formato HH:MM:SS ou MM:SS, retorna diretamente (ou simplificado)
  if (time.includes(':')) {
    const parts = time.split(':')
    if (parts.length === 3) {
      // HH:MM:SS - remover horas se for 00
      const hours = parseInt(parts[0], 10)
      const mins = parseInt(parts[1], 10)
      const secs = parseInt(parts[2], 10)
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
    // MM:SS - retorna como está
    return time
  }
  
  // Se for número de segundos
  const seconds = parseFloat(time)
  if (isNaN(seconds)) return '0:00'
  
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function PatternCardBase({ pattern, onNavigateToSegment, style, compact = false, speakerMapping = {} }: {
  pattern: SpeechPattern
  onNavigateToSegment?: (segmentIndex: number) => void
  style?: React.CSSProperties
  compact?: boolean
  speakerMapping?: SpeakerMapping
}) {
  const config = PATTERN_CONFIG[pattern.type] || PATTERN_CONFIG.hesitation
  const Icon = config.icon
  const sigConfig = SIGNIFICANCE_CONFIG[pattern.significance] || SIGNIFICANCE_CONFIG.medium

  // Obter nome mapeado do speaker
  const speakerName = speakerMapping[pattern.speaker] || pattern.speaker

  return (
    <div style={{ ...style, paddingRight: compact ? 4 : 8, paddingBottom: 4 }}>
    <Paper
      elevation={0}
      sx={{
        minHeight: 'calc(100% - 4px)',
        border: '1px solid #e0e0e0',
        borderLeft: compact ? `3px solid ${config.color}` : `4px solid ${config.color}`,
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: compact ? 0 : 1
        }
      }}
    >
      <Box
        sx={{
          p: compact ? 1.5 : 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: compact ? 1 : 2
        }}
      >
        <Box
          sx={{
            width: compact ? 28 : 36,
            height: compact ? 28 : 36,
            borderRadius: '50%',
            bgcolor: config.bgColor,
            '#monkee-file-transcript-client &': { backgroundColor: `${config.bgColor} !important`, background: `${config.bgColor} !important` },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <Icon sx={{ color: config.color, fontSize: compact ? 16 : 20 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{
              bgcolor: '#f5f5f5',
              '#monkee-file-transcript-client &': { backgroundColor: '#f5f5f5 !important', background: '#f5f5f5 !important' },
              px: compact ? 0.75 : 1,
              py: 0.25,
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: compact ? '0.65rem' : '0.75rem'
            }}>
              {formatTime(pattern.startTime)}
            </Typography>
            <Chip
              label={config.label}
              size="small"
              sx={{
                bgcolor: config.bgColor,
                '#monkee-file-transcript-client &': { backgroundColor: `${config.bgColor} !important`, background: `${config.bgColor} !important` },
                color: config.color,
                fontWeight: 600,
                fontSize: compact ? '0.6rem' : '0.65rem',
                height: compact ? 18 : 20
              }}
            />
            {!compact && (
              <Chip
                label={sigConfig.label}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.6rem',
                  height: 18,
                  color: sigConfig.color,
                  borderColor: sigConfig.color
                }}
              />
            )}
          </Box>

          <Typography
            variant="body2"
            sx={{
              bgcolor: '#fafafa',
              '#monkee-file-transcript-client &': { backgroundColor: '#fafafa !important', background: '#fafafa !important' },
              p: compact ? 0.75 : 1,
              borderRadius: 1,
              fontStyle: 'italic',
              fontSize: compact ? '0.8rem' : '0.85rem',
              whiteSpace: 'pre-wrap',
              ...(compact && {
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              })
            }}
          >
            "{pattern.text}"
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: compact ? 0.5 : 1 }}>
            <PersonIcon sx={{ fontSize: compact ? 12 : 14, color: '#666' }} />
            <Typography variant="caption" sx={{ color: '#666', fontSize: compact ? '0.65rem' : '0.75rem' }}>
              {speakerName}
            </Typography>
          </Box>

          {/* Contexto sempre visível */}
          {pattern.context && (
            <Typography variant="body2" sx={{ color: '#666', fontSize: compact ? '0.75rem' : '0.8rem', mt: compact ? 0.75 : 1 }}>
              <strong>Contexto:</strong> {pattern.context}
            </Typography>
          )}
        </Box>

        {onNavigateToSegment && (
          <Tooltip title="Ir para o segmento" disableHoverListener={compact}>
            <IconButton
              size="small"
              onClick={() => onNavigateToSegment(pattern.segmentIndex)}
              sx={{
                color: config.color,
                '&:hover': { bgcolor: config.bgColor },
                p: compact ? 0.5 : 1
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: compact ? 16 : 20 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Paper>
    </div>
  )
}

// Memoized PatternCard para evitar re-renders desnecessários
const PatternCard = memo(PatternCardBase, (prevProps, nextProps) => {
  return (
    prevProps.pattern.id === nextProps.pattern.id &&
    prevProps.onNavigateToSegment === nextProps.onNavigateToSegment &&
    prevProps.compact === nextProps.compact
  )
})

export default function SpeechPatternPanel({ fileId, onNavigateToSegment, speakerMapping = {}, analysisTypeStatus, onAnalysisStarted, analysisOutdated }: Props) {
  const [reanalyzing, setReanalyzing] = React.useState(false)

  const handleReanalyze = async () => {
    setReanalyzing(true)
    try {
      await runOnDemandAnalysis(fileId, 'patterns')
      onAnalysisStarted?.()
    } finally {
      setReanalyzing(false)
    }
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [analysis, setAnalysis] = useState<SpeechPatternAnalysis | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [filter, setFilter] = useState<SpeechPatternType | 'all'>('all')
  const [viewTab, setViewTab] = useState(0)

  const loadAnalysis = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getSpeechPatternAnalysis(fileId)
      setAnalysis(data)
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
      console.error('Erro ao carregar análise:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (analysisTypeStatus === 'SKIPPED' || analysisTypeStatus === 'PROCESSING') return
    loadAnalysis()
  }, [fileId, analysisTypeStatus])

  if (analysisTypeStatus === 'SKIPPED') {
    return (
      <AnalysisSkippedCard
        title="Análise de Padrões de Fala"
        analysisType="patterns"
        fileId={fileId}
        onAnalysisStarted={onAnalysisStarted}
      />
    )
  }

  if (analysisTypeStatus === 'PROCESSING') {
    return <AnalysisProcessingCard title="Análise de Padrões de Fala" />
  }

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
          <CircularProgress size={isMobile ? 36 : 48} />
          <Typography sx={{ mt: 2, color: '#666', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            Analisando padrões de fala...
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa', '--monkee-bg': '#fafafa', '#monkee-file-transcript-client &': { '--monkee-bg': '#fafafa', backgroundColor: '#fafafa !important', background: '#fafafa !important' } }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
          <Box sx={{ fontSize: { xs: 36, sm: 48 }, mb: 2 }}>⚠️</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {error.title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {error.message}
          </Typography>
          {error.canRetry && (
            isMobile ? (
              <IconButton onClick={loadAnalysis} color="primary">
                <RefreshIcon />
              </IconButton>
            ) : (
              <Button variant="outlined" onClick={loadAnalysis} startIcon={<RefreshIcon />}>
                Tentar novamente
              </Button>
            )
          )}
        </CardContent>
      </Card>
    )
  }

  if (!analysis) {
    return (
      <AnalysisNotAvailableCard 
        title="Análise de Padrões de Fala" 
        onRetry={loadAnalysis} 
      />
    )
  }

  const filteredPatterns = filter === 'all'
    ? analysis.patterns
    : analysis.byType[filter] || []

  const patternTypes = Object.keys(PATTERN_CONFIG) as SpeechPatternType[]
  const speakers = Object.keys(analysis.bySpeaker)

  return (
    <Box>
      {analysisOutdated && (
        <AnalysisOutdatedBanner
          analysisType="patterns"
          onReanalyze={handleReanalyze}
          isReanalyzing={reanalyzing}
        />
      )}
      {/* Header */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GraphicEqIcon sx={{ color: '#9c27b0', fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                {isMobile ? 'Padrões de Fala' : 'Análise de Padrões de Fala'}
              </Typography>
            </Box>
            {isMobile ? (
              <Tooltip title="Atualizar">
                <IconButton onClick={loadAnalysis} size="small">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                startIcon={<RefreshIcon />}
                onClick={loadAnalysis}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Atualizar
              </Button>
            )}
          </Box>

          {/* Estatísticas gerais */}
          <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center', bgcolor: '#f5f5f5', '--monkee-bg': '#f5f5f5', '#monkee-file-transcript-client &': { '--monkee-bg': '#f5f5f5', backgroundColor: '#f5f5f5 !important', background: '#f5f5f5 !important' } }}>
                <Typography sx={{ fontWeight: 700, color: '#9c27b0', fontSize: { xs: '1.25rem', sm: '2rem' } }}>
                  {analysis.statistics.totalPatterns}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                  {isMobile ? 'Total' : 'Padrões Detectados'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center', bgcolor: '#ffebee', '--monkee-bg': '#ffebee', '#monkee-file-transcript-client &': { '--monkee-bg': '#ffebee', backgroundColor: '#ffebee !important', background: '#ffebee !important' } }}>
                <Typography sx={{ fontWeight: 700, color: '#d32f2f', fontSize: { xs: '1.25rem', sm: '2rem' } }}>
                  {analysis.patterns.filter(p => p.significance === 'high').length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                  {isMobile ? 'Alta' : 'Alta Significância'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center', bgcolor: '#f3e5f5', '--monkee-bg': '#f3e5f5', '#monkee-file-transcript-client &': { '--monkee-bg': '#f3e5f5', backgroundColor: '#f3e5f5 !important', background: '#f3e5f5 !important' } }}>
                <Typography sx={{ fontWeight: 700, color: '#7b1fa2', fontSize: { xs: '0.75rem', sm: '2rem' } }}>
                  {analysis.statistics.mostCommonType ? PATTERN_CONFIG[analysis.statistics.mostCommonType]?.label || '-' : '-'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                  {isMobile ? 'Comum' : 'Padrão Mais Comum'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center', bgcolor: '#e3f2fd', '--monkee-bg': '#e3f2fd', '#monkee-file-transcript-client &': { '--monkee-bg': '#e3f2fd', backgroundColor: '#e3f2fd !important', background: '#e3f2fd !important' } }}>
                <Typography sx={{ fontWeight: 700, color: '#1976d2', fontSize: { xs: '0.75rem', sm: '2rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {analysis.statistics.speakerWithMostPatterns
                    ? (speakerMapping[analysis.statistics.speakerWithMostPatterns] || analysis.statistics.speakerWithMostPatterns)
                    : '-'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                  {isMobile ? 'Falante' : 'Mais Padrões'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Tabs
            value={viewTab}
            onChange={(_, v) => setViewTab(v)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTabs-indicator': { backgroundColor: '#1976d2' },
              '#monkee-file-transcript-client & .MuiTabs-indicator': {
                backgroundColor: '#1976d2 !important',
                height: '2px !important',
                display: 'block !important'
              },
              '#monkee-file-transcript-client & .MuiButtonBase-root': {
                backgroundColor: 'transparent !important'
              },
              '#monkee-file-transcript-client & .MuiTouchRipple-root': {
                display: 'block !important',
                overflow: 'hidden !important'
              }
            }}
            variant={isMobile ? 'fullWidth' : 'standard'}
          >
            <Tab
              label={isMobile ? 'Tipo' : 'Por Tipo'}
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minWidth: 'auto',
                '&.Mui-selected': { color: '#1976d2' },
                '#monkee-file-transcript-client &': { backgroundColor: 'transparent !important' },
                '#monkee-file-transcript-client &.Mui-selected': { color: '#1976d2 !important' }
              }}
            />
            <Tab
              label={isMobile ? 'Falante' : 'Por Falante'}
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minWidth: 'auto',
                '&.Mui-selected': { color: '#1976d2' },
                '#monkee-file-transcript-client &': { backgroundColor: 'transparent !important' },
                '#monkee-file-transcript-client &.Mui-selected': { color: '#1976d2 !important' }
              }}
            />
            <Tab
              label="Insights"
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minWidth: 'auto',
                '&.Mui-selected': { color: '#1976d2' },
                '#monkee-file-transcript-client &': { backgroundColor: 'transparent !important' },
                '#monkee-file-transcript-client &.Mui-selected': { color: '#1976d2 !important' }
              }}
            />
          </Tabs>
        </CardContent>
      </Card>

      {/* Tab: Por Tipo */}
      {viewTab === 0 && (
        <>
          {/* Filtros por tipo */}
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 2 }, fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Filtrar por Tipo
              </Typography>
              <Box
                sx={{
                  overflowX: 'auto',
                  pb: isMobile ? 1 : 0,
                  mx: { xs: -1.5, sm: 0 },
                  px: { xs: 1.5, sm: 0 },
                  '&::-webkit-scrollbar': { height: 4 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 2 }
                }}
              >
                <Stack direction="row" gap={0.5} sx={{ minWidth: 'max-content' }}>
                  <Chip
                    label={isMobile ? `Todos (${analysis.patterns.length})` : `Todos (${analysis.patterns.length})`}
                    onClick={() => setFilter('all')}
                    color={filter === 'all' ? 'primary' : 'default'}
                    variant={filter === 'all' ? 'filled' : 'outlined'}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                  />
                  {patternTypes.map((type) => {
                    const config = PATTERN_CONFIG[type]
                    const Icon = config.icon
                    const count = analysis.byType[type]?.length || 0
                    if (count === 0) return null

                    return (
                      <Chip
                        key={type}
                        icon={isMobile ? undefined : <Icon />}
                        label={`${isMobile ? config.label.substring(0, 4) : config.label} (${count})`}
                        onClick={() => setFilter(type)}
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                          bgcolor: filter === type ? config.bgColor : undefined,
                          color: filter === type ? config.color : undefined,
                          borderColor: filter === type ? config.color : undefined,
                          '& .MuiChip-icon': { color: config.color },
                          fontSize: { xs: '0.7rem', sm: '0.8125rem' }
                        }}
                        variant={filter === type ? 'filled' : 'outlined'}
                      />
                    )
                  })}
                </Stack>
              </Box>
            </CardContent>
          </Card>

          {/* Lista de padrões */}
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 2 }, fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {filter === 'all'
                  ? isMobile ? `Padrões (${filteredPatterns.length})` : `Todos os Padrões (${filteredPatterns.length})`
                  : `${PATTERN_CONFIG[filter].label} (${filteredPatterns.length})`
                }
              </Typography>

              {filteredPatterns.length > 0 ? (
                <VirtualizedAnalysisList
                  items={filteredPatterns}
                  renderItem={(pattern, index, style) => (
                    <PatternCard
                      key={pattern.id}
                      pattern={pattern}
                      onNavigateToSegment={onNavigateToSegment}
                      style={style}
                      compact={isMobile}
                      speakerMapping={speakerMapping}
                    />
                  )}
                  getItemHeight={(pattern) => {
                    // Altura fixa: sempre mostra contexto
                    if (isMobile) {
                      const baseHeight = 110
                      const contextLines = Math.ceil((pattern.context?.length || 0) / 40)
                      return baseHeight + (contextLines * 16)
                    }
                    const baseHeight = 140
                    const contextLines = Math.ceil((pattern.context?.length || 0) / 60)
                    return baseHeight + (contextLines * 20)
                  }}
                  minHeight={isMobile ? 280 : 400}
                  usePageScroll={true}
                />
              ) : (
                <Typography sx={{ textAlign: 'center', color: '#666', py: { xs: 3, sm: 4 }, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                  Nenhum padrão encontrado nesta categoria
                </Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Tab: Por Falante */}
      {viewTab === 1 && (
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Typography variant="subtitle2" sx={{ mb: { xs: 2, sm: 3 }, fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {isMobile ? 'Por Falante' : 'Análise por Falante'}
            </Typography>

            {speakers.map((speaker) => {
              const speakerPatterns = analysis.bySpeaker[speaker] || []
              const total = speakerPatterns.length
              const percentage = (total / analysis.patterns.length) * 100

              // Contar por tipo
              const typeCounts: Record<string, number> = {}
              speakerPatterns.forEach(p => {
                typeCounts[p.type] = (typeCounts[p.type] || 0) + 1
              })

              return (
                <Paper
                  key={speaker}
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    mb: { xs: 1.5, sm: 2 },
                    border: '1px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: { xs: 1, sm: 2 } }}>
                    <Box
                      sx={{
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        borderRadius: '50%',
                        bgcolor: '#e3f2fd',
                        '#monkee-file-transcript-client &': { backgroundColor: '#e3f2fd !important', background: '#e3f2fd !important' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PersonIcon sx={{ color: '#1976d2', fontSize: { xs: 18, sm: 24 } }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {speakerMapping[speaker] || speaker}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                        {total} padrões ({percentage.toFixed(0)}%)
                      </Typography>
                    </Box>
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: { xs: 6, sm: 8 },
                      borderRadius: 4,
                      mb: { xs: 1, sm: 2 },
                      bgcolor: '#e0e0e0',
                      '#monkee-file-transcript-client &': { backgroundColor: '#e0e0e0 !important', background: '#e0e0e0 !important' },
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#9c27b0'
                      }
                    }}
                  />

                  <Box
                    sx={{
                      overflowX: 'auto',
                      mx: { xs: -1.5, sm: 0 },
                      px: { xs: 1.5, sm: 0 },
                      '&::-webkit-scrollbar': { height: 4 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 2 }
                    }}
                  >
                    <Stack direction="row" flexWrap={isMobile ? 'nowrap' : 'wrap'} gap={0.5} sx={{ minWidth: isMobile ? 'max-content' : 'auto' }}>
                      {Object.entries(typeCounts).map(([type, count]) => {
                        const config = PATTERN_CONFIG[type as SpeechPatternType]
                        if (!config) return null
                        return (
                          <Chip
                            key={type}
                            size="small"
                            label={isMobile ? `${config.label.substring(0, 4)}: ${count}` : `${config.label}: ${count}`}
                            sx={{
                              bgcolor: config.bgColor,
                              '#monkee-file-transcript-client &': { backgroundColor: `${config.bgColor} !important`, background: `${config.bgColor} !important` },
                              color: config.color,
                              fontSize: { xs: '0.6rem', sm: '0.7rem' },
                              height: { xs: 20, sm: 24 }
                            }}
                          />
                        )
                      })}
                    </Stack>
                  </Box>
                </Paper>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* Tab: Insights */}
      {viewTab === 2 && (
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, sm: 3 } }}>
              <LightbulbIcon sx={{ color: '#ffc107', fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                {isMobile ? 'Insights' : 'Insights da Análise'}
              </Typography>
            </Box>

            {analysis.insights.map((insight, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  mb: { xs: 1, sm: 2 },
                  bgcolor: '#fffde7',
                  '--monkee-bg': '#fffde7',
                  '#monkee-file-transcript-client &': {
                    '--monkee-bg': '#fffde7',
                    backgroundColor: '#fffde7 !important',
                    background: '#fffde7 !important'
                  },
                  border: '1px solid #fff9c4',
                  borderRadius: 2
                }}
              >
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  💡 {insight}
                </Typography>
              </Paper>
            ))}

            {analysis.insights.length === 0 && (
              <Typography sx={{ textAlign: 'center', color: '#666', py: { xs: 3, sm: 4 }, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                Nenhum insight disponível
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Legenda */}
      {!isMobile && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', '#monkee-file-transcript-client &': { backgroundColor: '#f5f5f5 !important', background: '#f5f5f5 !important' }, borderRadius: 2 }}>
          <Typography variant="caption" sx={{ color: '#666' }}>
            💡 <strong>Dica:</strong> Padrões de fala podem indicar desconforto, incerteza ou tentativas de evasão.
            Padrões de alta significância merecem atenção especial durante a análise.
            {onNavigateToSegment && ' Use o botão ➔ para ir diretamente ao segmento na aba Transcrição.'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
